import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminAPI } from '../../shared/api/admin-api';
import AnimatedList from '../../shared/components/AnimatedList';
import AnimatedItem from '../../shared/components/AnimatedItem';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

interface Order {
  id: number;
  order_code: string;
  customer_name: string;
  total: number;
  status: string;
  created_at: string;
  items?: any[];
}

function Pedidos() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'processing' | 'delivered'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadOrders();
    // Polling de novos pedidos agora é feito pelo Service Worker
    // Não precisamos mais do setInterval aqui
  }, []);

  const loadOrders = async () => {
    try {
      const ordersData = await AdminAPI.get('orders') as Order[];
      setOrders(ordersData);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'pending') return order.status === 'pending';
    if (filter === 'processing') return order.status === 'confirmed' || order.status === 'shipped';
    if (filter === 'delivered') return order.status === 'delivered';
    return true;
  });

  const getStatusClass = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: 'pending',
      confirmed: 'processing',
      shipped: 'processing',
      delivered: 'delivered',
      cancelled: 'pending',
    };
    return statusMap[status] || 'pending';
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: 'Pendente',
      confirmed: 'Processando',
      shipped: 'Processando',
      delivered: 'Entregue',
      cancelled: 'Cancelado',
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return `Hoje, ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 2) {
      return `Ontem, ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <>
      {/* Loading Spinner - aparece apenas durante carregamento inicial */}
      {loading && <LoadingSpinner message="Carregando pedidos..." />}
      
      {/* Filters Section */}
      <section className="filters-section">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Todos
          </button>
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pendentes
          </button>
          <button
            className={`filter-btn ${filter === 'processing' ? 'active' : ''}`}
            onClick={() => setFilter('processing')}
          >
            Processando
          </button>
          <button
            className={`filter-btn ${filter === 'delivered' ? 'active' : ''}`}
            onClick={() => setFilter('delivered')}
          >
            Entregues
          </button>
        </div>
      </section>

      {/* Orders List */}
      <section className="orders-section">
        <AnimatedList
          listKey={`${filter}`}
          className="orders-list"
        >
          {filteredOrders.length === 0 ? (
            <div>Nenhum pedido encontrado</div>
          ) : (
            filteredOrders.map((order, index) => {
              const itemsCount = order.items?.length || 0;
              
              return (
                <AnimatedItem
                  key={order.id}
                  className="order-card"
                  index={index}
                >
                  <div className="order-header">
                    <div className="order-id">#{order.order_code || order.id}</div>
                    <div className={`order-status ${getStatusClass(order.status)}`}>
                      {getStatusText(order.status)}
                    </div>
                  </div>
                  <div className="order-info">
                    <div className="order-customer">
                      <i className="fas fa-user"></i>
                      {order.customer_name}
                    </div>
                    <div className="order-date">
                      <i className="fas fa-calendar"></i>
                      {formatDate(order.created_at)}
                    </div>
                    <div className="order-value">
                      <i className="fas fa-dollar-sign"></i>
                      R$ {parseFloat(String(order.total || 0)).toFixed(2).replace('.', ',')}
                    </div>
                  </div>
                  <div className="order-items">
                    <span className="item-count">{itemsCount} {itemsCount === 1 ? 'item' : 'itens'}</span>
                    <button 
                      className="view-btn"
                      onClick={() => navigate(`/admin/pedidos/${order.id}`)}
                    >
                      Ver Detalhes
                    </button>
                  </div>
                </AnimatedItem>
              );
            })
          )}
        </AnimatedList>
      </section>
    </>
  );
}

export default Pedidos;

